/**
 * Report metrics — the PURE engine behind the Reports tab.
 *
 * HubSpot/Databox-style reporting boiled down to one honest primitive:
 * `computeReportMetric(kind, data)` takes a metric kind and the app's real
 * collections (people, tickets, content, approvals, agents) and returns a
 * render-ready result — a headline number, a formatted display string, and
 * a small breakdown suitable for a bar list. NOTHING here fabricates data:
 *
 *   - Every number is derived from records the caller passes in, reusing
 *     the existing engines (scoreCustomerHealth for health bands,
 *     summarizeValueDelivered for the agent value ledger).
 *   - Ratios/averages over an empty denominator return `value: null` and
 *     display '—' instead of a fake 0 or NaN — "no data" is not "zero".
 *   - `empty` + `emptyHint` tell the UI when a card has nothing real to
 *     show so it can render an honest empty state.
 *
 * The metric REGISTRY (`REPORT_METRICS`) doubles as the build-your-own
 * report picker, and `DEFAULT_DASHBOARDS` defines the stock galleries
 * (Sales/Pipeline, Content, Customer Health, Agent/Autonomy, Delivery).
 * One-click report TEMPLATES that bundle these metrics into saved reports
 * live in src/lib/reports/reportTemplates.ts. The persisted
 * saved-report shape lives in src/lib/db/types.ts (`SavedReport`) with its
 * store wrapper in src/lib/db/savedReports.ts.
 */

import type {
  ApprovalItem,
  ContentItem,
  DeployedAgent,
  Person,
  Ticket,
} from '../db/types';
import { scoreCustomerHealth, type HealthBand } from '../crm/health';
import { summarizeValueDelivered } from '../agent/valueLedger';

// --- Vocabulary --------------------------------------------------------------

/** Every metric the Reports tab can compute. */
export type ReportMetricKind =
  // Sales / pipeline
  | 'pipeline-value'
  | 'open-prospects'
  | 'win-rate'
  | 'book-value'
  | 'avg-deal-size'
  | 'won-deals'
  // Content
  | 'content-published'
  | 'content-in-flight'
  | 'content-scheduled'
  | 'avg-content-score'
  // Customer health
  | 'active-customers'
  | 'avg-customer-health'
  | 'at-risk-customers'
  | 'healthy-customers'
  // Agent / autonomy + delivery
  | 'agent-runs'
  | 'approved-agent-actions'
  | 'agent-hours-saved'
  | 'agent-dollars-influenced'
  | 'pending-approvals'
  | 'open-tickets'
  | 'blocked-tickets'
  | 'shipped-tickets';

/** How a metric's headline number should be rendered. */
export type ReportMetricFormat = 'count' | 'currency' | 'percent' | 'score' | 'hours';

/** Which default dashboard a metric belongs to. */
export type ReportCategory = 'sales' | 'content' | 'health' | 'agents';

/** How a saved report renders: a single stat card or a bar breakdown. */
export type ReportDisplay = 'stat' | 'bars';

/** Registry entry — drives the builder's metric picker and card labels. */
export interface ReportMetricDef {
  kind: ReportMetricKind;
  label: string;
  description: string;
  category: ReportCategory;
  format: ReportMetricFormat;
}

/** One slice of a metric's breakdown (rendered as a bar list). */
export interface ReportBreakdownSlice {
  label: string;
  value: number;
}

/** The render-ready result of computing one metric. */
export interface ReportMetricResult {
  kind: ReportMetricKind;
  label: string;
  format: ReportMetricFormat;
  /**
   * The headline number. `null` means honestly unknowable — a rate or
   * average over an empty denominator — NEVER a fabricated zero.
   */
  value: number | null;
  /** `value` formatted for display ('—' when null). */
  display: string;
  /** Small labelled breakdown (may be empty). */
  breakdown: ReportBreakdownSlice[];
  /** True when there is no underlying data for this metric at all. */
  empty: boolean;
  /** Shown by the UI when `empty` — says what data would light this up. */
  emptyHint?: string;
}

/** Everything a metric may read. All collections optional — missing = []. */
export interface ReportData {
  people?: Person[];
  tickets?: Ticket[];
  contentItems?: ContentItem[];
  approvals?: ApprovalItem[];
  agents?: DeployedAgent[];
  /** Override "now" for deterministic health scoring in tests. */
  now?: number;
}

// --- Registry ----------------------------------------------------------------

export const REPORT_METRICS: readonly ReportMetricDef[] = [
  {
    kind: 'pipeline-value',
    label: 'Open pipeline value',
    description: 'Sum of estimated deal value across open prospects, by stage.',
    category: 'sales',
    format: 'currency',
  },
  {
    kind: 'open-prospects',
    label: 'Open prospects',
    description: 'Prospects still in play (new, qualified, proposal).',
    category: 'sales',
    format: 'count',
  },
  {
    kind: 'win-rate',
    label: 'Win rate',
    description: 'Closed-won over all closed prospects — computed over closed deals only.',
    category: 'sales',
    format: 'percent',
  },
  {
    kind: 'book-value',
    label: 'Book of business',
    description: 'Annualised account value across non-churned customers.',
    category: 'sales',
    format: 'currency',
  },
  {
    kind: 'avg-deal-size',
    label: 'Avg deal size',
    description: 'Mean estimated value across open prospects that have one.',
    category: 'sales',
    format: 'currency',
  },
  {
    kind: 'won-deals',
    label: 'Won deals',
    description: 'Prospects closed-won, with a won/lost breakdown.',
    category: 'sales',
    format: 'count',
  },
  {
    kind: 'content-published',
    label: 'Content published',
    description: 'Items that actually shipped (status: published).',
    category: 'content',
    format: 'count',
  },
  {
    kind: 'content-in-flight',
    label: 'Content in flight',
    description: 'Ideas, drafts, and scheduled items moving through the pipeline.',
    category: 'content',
    format: 'count',
  },
  {
    kind: 'content-scheduled',
    label: 'Scheduled content',
    description: 'Items slotted to go out (status: scheduled).',
    category: 'content',
    format: 'count',
  },
  {
    kind: 'avg-content-score',
    label: 'Avg content score',
    description: 'Mean best-practice score across scored items only.',
    category: 'content',
    format: 'score',
  },
  {
    kind: 'active-customers',
    label: 'Active customers',
    description: 'Live customer accounts (not paused, not churned).',
    category: 'health',
    format: 'count',
  },
  {
    kind: 'avg-customer-health',
    label: 'Avg customer health',
    description: 'Mean health score (0–100) across live customers.',
    category: 'health',
    format: 'score',
  },
  {
    kind: 'at-risk-customers',
    label: 'At-risk customers',
    description: 'Live customers whose health band is at-risk.',
    category: 'health',
    format: 'count',
  },
  {
    kind: 'healthy-customers',
    label: 'Healthy customers',
    description: 'Live customers whose health band is healthy.',
    category: 'health',
    format: 'count',
  },
  {
    kind: 'agent-runs',
    label: 'Agent runs',
    description: 'Completed runs across the deployed agent fleet.',
    category: 'agents',
    format: 'count',
  },
  {
    kind: 'approved-agent-actions',
    label: 'Approved agent actions',
    description: 'Delivered agent work a human approved — never pending proposals.',
    category: 'agents',
    format: 'count',
  },
  {
    kind: 'agent-hours-saved',
    label: 'Operator hours saved',
    description: 'Conservative estimate of hours saved by approved agent work.',
    category: 'agents',
    format: 'hours',
  },
  {
    kind: 'agent-dollars-influenced',
    label: 'Dollars influenced',
    description: 'USD touched by approved agent actions. Influenced, not banked.',
    category: 'agents',
    format: 'currency',
  },
  {
    kind: 'pending-approvals',
    label: 'Pending approvals',
    description: 'Agent proposals waiting on a human decision.',
    category: 'agents',
    format: 'count',
  },
  {
    kind: 'open-tickets',
    label: 'Open tickets',
    description: 'Dev tickets not yet done or cancelled.',
    category: 'agents',
    format: 'count',
  },
  {
    kind: 'blocked-tickets',
    label: 'Blocked tickets',
    description: 'Dev tickets currently stuck (status: blocked).',
    category: 'agents',
    format: 'count',
  },
  {
    kind: 'shipped-tickets',
    label: 'Shipped tickets',
    description: 'Dev tickets marked done.',
    category: 'agents',
    format: 'count',
  },
] as const;

/** Look up a metric definition; throws on an unknown kind (never render a lie). */
export function reportMetricDef(kind: ReportMetricKind): ReportMetricDef {
  const def = REPORT_METRICS.find((m) => m.kind === kind);
  if (!def) throw new Error(`Unknown report metric: ${kind}`);
  return def;
}

// --- Default dashboards --------------------------------------------------------

export interface DefaultDashboard {
  id: string;
  title: string;
  description: string;
  category: ReportCategory;
  metrics: ReportMetricKind[];
}

/** The stock gallery — every metric here exists in {@link REPORT_METRICS}. */
export const DEFAULT_DASHBOARDS: readonly DefaultDashboard[] = [
  {
    id: 'sales-pipeline',
    title: 'Sales & Pipeline',
    description: 'Open pipeline, deal flow, win rate, and the book of business.',
    category: 'sales',
    metrics: [
      'pipeline-value',
      'open-prospects',
      'avg-deal-size',
      'win-rate',
      'won-deals',
      'book-value',
    ],
  },
  {
    id: 'content',
    title: 'Content',
    description: 'What shipped, what is moving, and how it scores.',
    category: 'content',
    metrics: ['content-published', 'content-in-flight', 'content-scheduled', 'avg-content-score'],
  },
  {
    id: 'customer-health',
    title: 'Customer Health',
    description: 'Live accounts, average health, and who is at risk.',
    category: 'health',
    metrics: [
      'active-customers',
      'avg-customer-health',
      'at-risk-customers',
      'healthy-customers',
    ],
  },
  {
    id: 'agent-autonomy',
    title: 'Agent & Autonomy',
    description: 'The honest value ledger: runs, approved work, time and dollars.',
    category: 'agents',
    metrics: [
      'agent-runs',
      'approved-agent-actions',
      'pending-approvals',
      'agent-hours-saved',
      'agent-dollars-influenced',
    ],
  },
  {
    id: 'delivery',
    title: 'Delivery',
    description: 'Dev ticket flow — what is open, what is stuck, what shipped.',
    category: 'agents',
    metrics: ['open-tickets', 'blocked-tickets', 'shipped-tickets'],
  },
] as const;

// --- Formatting ----------------------------------------------------------------

/** Format a metric value for display. `null` renders as an honest em-dash. */
export function formatMetricValue(
  value: number | null,
  format: ReportMetricFormat,
): string {
  if (value === null) return '—';
  switch (format) {
    case 'currency':
      return `$${Math.round(value).toLocaleString('en-US')}`;
    case 'percent':
      return `${Math.round(value)}%`;
    case 'hours':
      return `${Math.round(value * 10) / 10}h`;
    case 'score':
      return `${Math.round(value)}`;
    case 'count':
      return value.toLocaleString('en-US');
  }
}

// --- Compute -------------------------------------------------------------------

const OPEN_PROSPECT_STAGES = ['new', 'qualified', 'proposal'] as const;
type OpenStage = (typeof OPEN_PROSPECT_STAGES)[number];

function isOpenStage(stage: string): stage is OpenStage {
  return (OPEN_PROSPECT_STAGES as readonly string[]).includes(stage);
}

function prospects(data: ReportData): Person[] {
  return (data.people ?? []).filter((p) => p.category === 'prospect');
}

/** Open prospects — a prospect with no stage recorded is honestly 'new'. */
function openProspects(data: ReportData): Person[] {
  return prospects(data).filter((p) => isOpenStage(p.stage ?? 'new'));
}

function customers(data: ReportData): Person[] {
  return (data.people ?? []).filter((p) => p.category === 'customer');
}

/** Live = customer that hasn't churned (paused still counts as on the book). */
function liveCustomers(data: ReportData): Person[] {
  return customers(data).filter((p) => p.status !== 'churned');
}

function healthBands(data: ReportData): Record<HealthBand, number> {
  const bands: Record<HealthBand, number> = { 'at-risk': 0, watch: 0, healthy: 0 };
  for (const p of liveCustomers(data)) {
    bands[scoreCustomerHealth(p, { now: data.now }).band] += 1;
  }
  return bands;
}

function result(
  kind: ReportMetricKind,
  value: number | null,
  breakdown: ReportBreakdownSlice[],
  empty: boolean,
  emptyHint?: string,
): ReportMetricResult {
  const def = reportMetricDef(kind);
  return {
    kind,
    label: def.label,
    format: def.format,
    value,
    display: formatMetricValue(value, def.format),
    breakdown,
    empty,
    ...(empty && emptyHint ? { emptyHint } : {}),
  };
}

/**
 * Compute one metric from real data. Pure and deterministic (pass `now`
 * for health metrics). See the honesty rules in the module doc.
 */
export function computeReportMetric(
  kind: ReportMetricKind,
  data: ReportData,
): ReportMetricResult {
  switch (kind) {
    case 'pipeline-value': {
      const open = openProspects(data);
      const byStage = OPEN_PROSPECT_STAGES.map((stage) => ({
        label: stage,
        value: open
          .filter((p) => (p.stage ?? 'new') === stage)
          .reduce((sum, p) => sum + (p.estimatedValue ?? 0), 0),
      })).filter((s) => s.value > 0);
      const total = open.reduce((sum, p) => sum + (p.estimatedValue ?? 0), 0);
      return result(kind, total, byStage, open.length === 0, 'Add prospects with estimated deal values.');
    }

    case 'open-prospects': {
      const open = openProspects(data);
      const byStage = OPEN_PROSPECT_STAGES.map((stage) => ({
        label: stage,
        value: open.filter((p) => (p.stage ?? 'new') === stage).length,
      })).filter((s) => s.value > 0);
      return result(kind, open.length, byStage, open.length === 0, 'No prospects in play yet.');
    }

    case 'win-rate': {
      const all = prospects(data);
      const won = all.filter((p) => p.stage === 'closed-won').length;
      const lost = all.filter((p) => p.stage === 'closed-lost').length;
      const closed = won + lost;
      const breakdown = [
        { label: 'won', value: won },
        { label: 'lost', value: lost },
      ].filter((s) => s.value > 0);
      // No closed deals → the rate is unknowable, not 0%.
      return result(
        kind,
        closed === 0 ? null : (won / closed) * 100,
        breakdown,
        closed === 0,
        'No closed deals yet — the rate would be a guess.',
      );
    }

    case 'avg-deal-size': {
      // Only open prospects that actually carry an estimate — an average
      // over deals without values would be a guess, not a metric.
      const valued = openProspects(data).filter((p) => (p.estimatedValue ?? 0) > 0);
      const avg =
        valued.length === 0
          ? null
          : valued.reduce((sum, p) => sum + (p.estimatedValue ?? 0), 0) / valued.length;
      return result(
        kind,
        avg,
        [],
        valued.length === 0,
        'Add estimated deal values to open prospects.',
      );
    }

    case 'won-deals': {
      const all = prospects(data);
      const won = all.filter((p) => p.stage === 'closed-won').length;
      const lost = all.filter((p) => p.stage === 'closed-lost').length;
      const breakdown = [
        { label: 'won', value: won },
        { label: 'lost', value: lost },
      ].filter((s) => s.value > 0);
      return result(kind, won, breakdown, all.length === 0, 'No prospects yet.');
    }

    case 'book-value': {
      const live = liveCustomers(data);
      const top = live
        .filter((p) => (p.accountValue ?? 0) > 0)
        .sort((a, b) => (b.accountValue ?? 0) - (a.accountValue ?? 0))
        .slice(0, 5)
        .map((p) => ({ label: p.name, value: p.accountValue ?? 0 }));
      const total = live.reduce((sum, p) => sum + (p.accountValue ?? 0), 0);
      return result(kind, total, top, live.length === 0, 'No live customers on the book.');
    }

    case 'content-published': {
      const items = data.contentItems ?? [];
      const published = items.filter((i) => i.status === 'published').length;
      return result(kind, published, [], items.length === 0, 'Nothing in the content pipeline yet.');
    }

    case 'content-in-flight': {
      const items = data.contentItems ?? [];
      const flight = (['idea', 'draft', 'scheduled'] as const).map((status) => ({
        label: status,
        value: items.filter((i) => i.status === status).length,
      }));
      const total = flight.reduce((sum, s) => sum + s.value, 0);
      return result(
        kind,
        total,
        flight.filter((s) => s.value > 0),
        items.length === 0,
        'Nothing in the content pipeline yet.',
      );
    }

    case 'content-scheduled': {
      const items = data.contentItems ?? [];
      const scheduled = items.filter((i) => i.status === 'scheduled').length;
      return result(
        kind,
        scheduled,
        [],
        items.length === 0,
        'Nothing in the content pipeline yet.',
      );
    }

    case 'avg-content-score': {
      const scored = (data.contentItems ?? []).filter((i) => i.score !== null);
      const avg =
        scored.length === 0
          ? null
          : scored.reduce((sum, i) => sum + (i.score ?? 0), 0) / scored.length;
      return result(kind, avg, [], scored.length === 0, 'No scored content yet.');
    }

    case 'active-customers': {
      const all = customers(data);
      const byStatus = (['active', 'paused', 'churned'] as const)
        .map((status) => ({
          label: status,
          value: all.filter((p) => (p.status ?? 'active') === status).length,
        }))
        .filter((s) => s.value > 0);
      const active = all.filter((p) => (p.status ?? 'active') === 'active').length;
      return result(kind, active, byStatus, all.length === 0, 'No customers yet.');
    }

    case 'avg-customer-health': {
      const live = liveCustomers(data);
      const bands = healthBands(data);
      const avg =
        live.length === 0
          ? null
          : live.reduce(
              (sum, p) => sum + scoreCustomerHealth(p, { now: data.now }).score,
              0,
            ) / live.length;
      const breakdown = (['at-risk', 'watch', 'healthy'] as const)
        .map((band) => ({ label: band, value: bands[band] }))
        .filter((s) => s.value > 0);
      return result(kind, avg, breakdown, live.length === 0, 'No live customers to score.');
    }

    case 'at-risk-customers': {
      const live = liveCustomers(data);
      const bands = healthBands(data);
      const breakdown = (['at-risk', 'watch', 'healthy'] as const)
        .map((band) => ({ label: band, value: bands[band] }))
        .filter((s) => s.value > 0);
      return result(kind, bands['at-risk'], breakdown, live.length === 0, 'No live customers to score.');
    }

    case 'healthy-customers': {
      const live = liveCustomers(data);
      const bands = healthBands(data);
      const breakdown = (['at-risk', 'watch', 'healthy'] as const)
        .map((band) => ({ label: band, value: bands[band] }))
        .filter((s) => s.value > 0);
      return result(kind, bands.healthy, breakdown, live.length === 0, 'No live customers to score.');
    }

    case 'agent-runs': {
      const fleet = data.agents ?? [];
      const total = fleet.reduce((sum, a) => sum + a.runCount, 0);
      const perAgent = fleet
        .filter((a) => a.runCount > 0)
        .map((a) => ({ label: a.name, value: a.runCount }));
      return result(kind, total, perAgent, fleet.length === 0, 'No agents deployed yet.');
    }

    case 'approved-agent-actions': {
      const summary = summarizeValueDelivered(data.approvals ?? []);
      const byKind = Object.entries(summary.byKind).map(([k, v]) => ({
        label: k.replace(/_/g, ' '),
        value: v.count,
      }));
      return result(
        kind,
        summary.approvedCount,
        byKind,
        summary.approvedCount === 0,
        'No approved agent work yet — pending proposals never count.',
      );
    }

    case 'agent-hours-saved': {
      const summary = summarizeValueDelivered(data.approvals ?? []);
      return result(
        kind,
        summary.hoursSaved,
        [],
        summary.approvedCount === 0,
        'No approved agent work yet — pending proposals never count.',
      );
    }

    case 'agent-dollars-influenced': {
      const summary = summarizeValueDelivered(data.approvals ?? []);
      return result(
        kind,
        summary.dollarsInfluenced,
        [],
        summary.approvedCount === 0,
        'No approved agent work yet — pending proposals never count.',
      );
    }

    case 'pending-approvals': {
      const all = data.approvals ?? [];
      const pending = all.filter((a) => a.status === 'pending');
      const byKind = new Map<string, number>();
      for (const a of pending) {
        const label = a.action.kind.replace(/_/g, ' ');
        byKind.set(label, (byKind.get(label) ?? 0) + 1);
      }
      const breakdown = [...byKind.entries()].map(([label, value]) => ({ label, value }));
      return result(
        kind,
        pending.length,
        breakdown,
        all.length === 0,
        'No agent proposals yet.',
      );
    }

    case 'open-tickets': {
      const tickets = data.tickets ?? [];
      const openStatuses = ['backlog', 'open', 'claimed', 'in-progress', 'blocked', 'review'] as const;
      const byStatus = openStatuses
        .map((status) => ({
          label: status,
          value: tickets.filter((t) => t.status === status).length,
        }))
        .filter((s) => s.value > 0);
      const open = byStatus.reduce((sum, s) => sum + s.value, 0);
      return result(kind, open, byStatus, tickets.length === 0, 'No dev tickets yet.');
    }

    case 'blocked-tickets': {
      const tickets = data.tickets ?? [];
      const blocked = tickets.filter((t) => t.status === 'blocked').length;
      return result(kind, blocked, [], tickets.length === 0, 'No dev tickets yet.');
    }

    case 'shipped-tickets': {
      const tickets = data.tickets ?? [];
      const done = tickets.filter((t) => t.status === 'done').length;
      return result(kind, done, [], tickets.length === 0, 'No dev tickets yet.');
    }
  }
}
